import {
	CheckboxField,
	CreateDrawerFormProps,
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	FormFetchDrawer,
	SingleSelectField,
	TextField,
	UpdateDrawerFormProps,
} from "@dgs/core";
import { RoleShowDto } from "apiGen";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { roleService } from "~shared/api/roles/roleService";
import { IPermission } from "~shared/types/permission";
import {
	payrollsVisibilityOptionNames,
	timesVisibilityOptionNames as timesVisibilityOptionNames,
	vacationsVisibilityOptionNames as vacationsVisibilityOptionNames,
} from "./permissions";

const Separator = styled.div`
	height: 20px;
`;

export type IRoleFormState = Omit<RoleShowDto, "id" | "permissions" | "users"> & {
	permissions: Record<string, IPermission & { enabled: boolean }>;
};

const useValidate = () => {
	const { t } = useTranslation();

	return (values: IRoleFormState) => {
		const errors: { [K in keyof IRoleFormState]?: any } = {};

		if (!values.name) {
			errors.name = t("Field is required");
		}
		if (!values.permissions) {
			errors.permissions = t("Field is required");
		}
		if (!values.payrollsVisibility) {
			errors.payrollsVisibility = t("Field is required");
		}
		return errors;
	};
};

interface RoleDrawerContentProps extends FormDrawerContentProps {
	availablePermissions: IPermission[];
}

const RoleDrawerContent: FC<RoleDrawerContentProps> = ({ availablePermissions, ...props }) => {
	const { t } = useTranslation();
	const vacationsVisibilityOptions = availablePermissions.filter(({ name }) =>
		vacationsVisibilityOptionNames.includes(name),
	);
	const timesVisibilityOptions = availablePermissions.filter(({ name }) => timesVisibilityOptionNames.includes(name));
	const payrollsVisibilityOptions = availablePermissions.filter(({ name }) =>
		payrollsVisibilityOptionNames.includes(name),
	);
	const getLabel = useCallback(
		({ name }: IPermission) => {
			const parts = name.split(".");
			return t(parts[parts.length - 1]);
		},
		[t],
	);

	return (
		<FormDrawerContent {...props}>
			<TextField name="name" label={t("Name")} required />
			<HeaderRow>
				<NameCell>{t("Name")}</NameCell>
				<div>{t("Read")}</div>
				<div>{t("Create")}</div>
				<div>{t("Edit")}</div>
				<div>{t("Delete")}</div>
			</HeaderRow>
			<CrudRow name={"Permissions.Employees"} label={t("Employees")} />
			<CrudRow name={"Permissions.Accounts"} label={t("Accounts")} />
			<CrudRow name={"Permissions.Vacations"} label={t("Vacations")} />
			<CrudRow name={"Permissions.Projects"} label={t("Projects")} />
			<CrudRow name={"Permissions.Activities"} label={t("Activities")} />
			<CrudRow name={"Permissions.Roles"} label={t("Roles")} />
			<CrudRow name={"Permissions.Users"} label={t("Users")} />
			<CrudRow name={"Permissions.OffDays"} label={t("Off days")} />
			<CrudRow name={"Permissions.Branches"} label={t("Branches")} />
			<CrudRow name={"Permissions.Teams"} label={t("Teams")} />
			<CrudRow name={"Permissions.Times"} label={t("Times")} />
			<CrudRow name={"Permissions.Payrolls"} label={t("Payrolls")} />
			<Separator />
			<SingleSelectField
				name="vacationsVisibility"
				options={vacationsVisibilityOptions}
				label={t("Vacations visibility")}
				getLabel={getLabel}
			/>
			<SingleSelectField
				name="timesVisibility"
				options={timesVisibilityOptions}
				label={t("Times visibility")}
				getLabel={getLabel}
			/>
			<SingleSelectField
				name="payrollsVisibility"
				options={payrollsVisibilityOptions}
				label={t("Payrolls visibility")}
				getLabel={getLabel}
			/>
		</FormDrawerContent>
	);
};

interface RoleCreatDrawerProps extends CreateDrawerFormProps<IRoleFormState> {
	availablePermissions: IPermission[];
}

export const RoleCreateDrawer: FC<RoleCreatDrawerProps> = ({ availablePermissions, ...drawerProps }) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const initialValues: IRoleFormState = {
		name: "",
		vacationsVisibility: availablePermissions.find(
			({ name }) => name === "Permissions.Vacations.Visibility.All",
		) as IPermission,
		timesVisibility: availablePermissions.find(
			({ name }) => name === "Permissions.Times.Visibility.All",
		) as IPermission,
		payrollsVisibility: availablePermissions.find(({ name }) => name === "Permissions.Payrolls.Visibility.All"),
		permissions: availablePermissions.reduce(
			(acc, permission) => {
				return {
					...acc,
					[permission.name]: {
						...permission,
						enabled: false,
					},
				};
			},
			{} as IRoleFormState["permissions"],
		),
	};
	return (
		<FormDrawer
			{...drawerProps}
			title={t("Add role")}
			validate={validate}
			initialValues={initialValues}
			renderForm={(props) => <RoleDrawerContent {...props} availablePermissions={availablePermissions} />}
		/>
	);
};

interface RoleUpdateDrawerProps extends UpdateDrawerFormProps<IRoleFormState> {
	availablePermissions: IPermission[];
}

const mapToFormState = (entity: RoleShowDto, permissions: IPermission[]): IRoleFormState => {
	return {
		name: entity.name,
		vacationsVisibility: entity.vacationsVisibility,
		timesVisibility: entity.timesVisibility,
		payrollsVisibility: entity.payrollsVisibility,
		permissions: permissions.reduce(
			(acc, permission) => {
				if (
					[...vacationsVisibilityOptionNames, ...timesVisibilityOptionNames, ...payrollsVisibilityOptionNames].includes(
						permission.name,
					)
				)
					return acc;
				return {
					...acc,
					[permission.name]: {
						...permission,
						enabled: entity.permissions.some((x) => x.id === permission.id),
					},
				};
			},
			{} as Record<string, IPermission & { enabled: boolean }>,
		),
	};
};

export const RoleUpdateDrawer: FC<RoleUpdateDrawerProps> = ({ availablePermissions, ...drawerProps }) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormFetchDrawer
			{...drawerProps}
			queryFn={() => roleService.show(drawerProps.id).then((x) => mapToFormState(x.data.data, availablePermissions))}
			queryKey={[...roleService.showKey(drawerProps.id), "formState"]}
			title={t("Edit role")}
			validate={validate}
			renderSuccess={(props) => <RoleDrawerContent {...props} availablePermissions={availablePermissions} />}
		/>
	);
};

const NameCell = styled.div`
	justify-self: start;
`;

const Row = styled.div`
	display: grid;
	grid-template-columns: 100px 1fr 1fr 1fr 1fr;
	gap: ${({ theme }) => theme.spacing(1)};
	border-bottom: 1px solid #ccc;

	> * {
		display: flex;
		justify-content: center;
		align-items: center;
	}
`;

const HeaderRow = styled(Row)`
	padding-bottom: ${({ theme }) => theme.spacing(3)};
`;
const StyledCheckboxField = styled(CheckboxField)`
	margin: 12px;
`;
const CrudRow: FC<{ name: string; label: string }> = ({ name, label }) => {
	return (
		<Row>
			<NameCell>{label}</NameCell>
			<div>
				<StyledCheckboxField label="" name={`permissions.['${name}.Read'].enabled`} />
			</div>
			<div>
				<StyledCheckboxField label="" name={`permissions.['${name}.Create'].enabled`} />
			</div>
			<div>
				<StyledCheckboxField label="" name={`permissions.['${name}.Update'].enabled`} />
			</div>
			<div>
				<StyledCheckboxField label="" name={`permissions.['${name}.Delete'].enabled`} />
			</div>
		</Row>
	);
};
